/**
 * DateRangePicker — preset period chips (7d/30d/90d) plus Custom chip.
 * Custom chip opens a popover with quick presets, From/To date inputs,
 * and Apply/Cancel buttons. Active custom range displays as human-readable
 * date range (e.g. "Mar 1 - Mar 31").
 */

import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  differenceInDays,
} from 'date-fns';
import { Pencil } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
// DayPicker from react-day-picker available for future calendar upgrade
// Currently using native date inputs for simplicity
import { DayPicker as _DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// Re-export to suppress unused import (reserved for calendar mode)
export type { _DayPicker as DayPickerType };

// ============================================================================
// CONSTANTS
// ============================================================================

const PRESET_PERIODS = ['7d', '30d', '90d'] as const;

interface QuickPreset {
  label: string;
  from: () => Date;
  to: () => Date;
}

const QUICK_PRESETS: QuickPreset[] = [
  { label: 'Last 14d', from: () => subDays(new Date(), 14), to: () => new Date() },
  { label: 'Last 60d', from: () => subDays(new Date(), 60), to: () => new Date() },
  { label: 'This month', from: () => startOfMonth(new Date()), to: () => new Date() },
  {
    label: 'Last month',
    from: () => startOfMonth(subMonths(new Date(), 1)),
    to: () => endOfMonth(subMonths(new Date(), 1)),
  },
  { label: 'This quarter', from: () => startOfQuarter(new Date()), to: () => new Date() },
];

// ============================================================================
// HELPERS
// ============================================================================

function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function isValidRange(from: string, to: string): boolean {
  if (!from || !to) return false;
  if (from > to) return false;
  const diff = differenceInDays(new Date(to), new Date(from));
  return diff <= 365;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function DateRangePicker() {
  const { filters, setCustomDateRange, clearCustomDateRange } = useDashboardContext();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    }
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popoverOpen]);

  // Open popover with current or default dates
  const openPopover = useCallback(() => {
    if (filters.period === 'custom' && filters.fromDate && filters.toDate) {
      setFromInput(filters.fromDate);
      setToInput(filters.toDate);
    } else {
      // Default to last 30 days
      setFromInput(toISODate(subDays(new Date(), 30)));
      setToInput(toISODate(new Date()));
    }
    setPopoverOpen(true);
  }, [filters.period, filters.fromDate, filters.toDate]);

  // Apply quick preset
  const applyQuickPreset = useCallback((preset: QuickPreset) => {
    setFromInput(toISODate(preset.from()));
    setToInput(toISODate(preset.to()));
  }, []);

  // Apply custom range
  const handleApply = useCallback(() => {
    if (isValidRange(fromInput, toInput)) {
      setCustomDateRange(fromInput, toInput);
      setPopoverOpen(false);
    }
  }, [fromInput, toInput, setCustomDateRange]);

  // Cancel
  const handleCancel = useCallback(() => {
    setPopoverOpen(false);
    setFromInput('');
    setToInput('');
  }, []);

  // Format active custom range for display
  const customRangeLabel =
    filters.period === 'custom' && filters.fromDate && filters.toDate
      ? `${format(new Date(filters.fromDate), 'MMM d')} - ${format(new Date(filters.toDate), 'MMM d')}`
      : null;

  const rangeValid = isValidRange(fromInput, toInput);

  return (
    <div className="relative flex items-center gap-0.5" ref={containerRef}>
      {/* Part A: Preset chips */}
      {PRESET_PERIODS.map((p) => (
        <button
          key={p}
          onClick={() => clearCustomDateRange(p)}
          className={cn(
            'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
            filters.period === p
              ? 'bg-brand text-inverse'
              : 'text-secondary hover:text-primary hover:bg-surface-subtle'
          )}
        >
          {p}
        </button>
      ))}

      {/* Part B: Custom chip */}
      {filters.period === 'custom' && customRangeLabel ? (
        <button
          onClick={openPopover}
          className="inline-flex items-center gap-1 bg-brand text-inverse rounded-md px-2.5 py-1 text-xs font-medium cursor-pointer"
        >
          {customRangeLabel}
          <Pencil className="w-3 h-3" />
        </button>
      ) : (
        filters.period !== 'custom' && (
          <button
            onClick={openPopover}
            className="border border-dashed border-border-subtle text-secondary hover:text-primary hover:border-primary rounded-md px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors"
          >
            Custom
          </button>
        )
      )}

      {/* Part C: Custom date popover */}
      {popoverOpen && (
        <div className="absolute top-full right-0 mt-2 z-50 w-80 rounded-xl border border-border-subtle bg-surface shadow-lg p-4">
          {/* Quick presets row */}
          <div className="flex flex-wrap gap-2 mb-3">
            {QUICK_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyQuickPreset(preset)}
                className="text-xs text-brand hover:underline cursor-pointer"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* From input */}
          <div className="mb-2">
            <label className="text-xs text-secondary block mb-1">From</label>
            <input
              type="date"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-border-subtle rounded-lg bg-surface focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {/* To input */}
          <div className="mb-3">
            <label className="text-xs text-secondary block mb-1">To</label>
            <input
              type="date"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-border-subtle rounded-lg bg-surface focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {/* Validation hint */}
          {fromInput && toInput && !rangeValid && (
            <p className="text-xs text-danger mb-2">
              {fromInput > toInput
                ? 'From date must be before To date'
                : 'Date range cannot exceed 365 days'}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleCancel}
              className="text-xs text-secondary hover:text-primary px-3 py-1.5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!rangeValid}
              className={cn(
                'text-xs text-inverse bg-brand px-3 py-1.5 rounded-lg font-medium transition-colors',
                rangeValid ? 'hover:bg-brand/90' : 'opacity-50 cursor-not-allowed'
              )}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
